import type {
  ApprovalMode,
  ApprovalNodeType,
  DocumentStatus,
  DocumentType,
  RoleCode,
} from '@hsl-ems/contracts';

const apiBase = `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'}/api/v1`;

const resourceFor: Record<DocumentType, string> = {
  EXPENSE_REIMBURSEMENT: 'reimbursements',
  LOAN: 'loans',
  CASH_REPAYMENT: 'cash-repayments',
  LOAN_OFFSET: 'loan-offsets',
  CORPORATE_PAYMENT: 'corporate-payments',
};

export type ApprovalTaskStatus =
  'PENDING' | 'APPROVED' | 'RETURNED' | 'REJECTED' | 'SKIPPED' | 'CANCELLED';

export interface ApprovalTaskSummary {
  id: string;
  status: ApprovalTaskStatus;
  version: number;
  dueAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  node: { id: string; sequence: number; name: string; approvalMode: 'ANY_ONE' | 'ALL' };
  instance: {
    id: string;
    status: string;
    submissionNumber: number;
    document: {
      id: string;
      documentNo: string;
      type: DocumentType;
      status: DocumentStatus;
      requestedAmount: string;
      description: string;
      operatingOrgId: string;
      applicant: { id: string; name: string };
    };
  };
}

export interface ApprovalTaskDetail {
  id: string;
  status: ApprovalTaskStatus;
  version: number;
  dueAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  node: {
    id: string;
    sequence: number;
    name: string;
    approvalMode: 'ANY_ONE' | 'ALL';
    nodeType: string;
  };
  assignee: { id: string; name: string };
  instance: {
    id: string;
    status: string;
    submissionNumber: number;
    currentNodeId: string | null;
    document: {
      id: string;
      documentNo: string;
      type: DocumentType;
      status: DocumentStatus;
      requestedAmount: string;
      approvedAmount: string | null;
      payableAmount: string | null;
      description: string;
      applicantUserId: string;
      operatingOrgId: string;
      organizationSnapshot: Record<string, unknown> | null;
      applicantSnapshot: Record<string, unknown> | null;
      payeeSnapshot: Record<string, unknown> | null;
      approvalSnapshot: Record<string, unknown> | null;
    };
    tasks: Array<{
      id: string;
      status: ApprovalTaskStatus;
      version: number;
      decidedAt: string | null;
      createdAt: string;
      node: { sequence: number; name: string; approvalMode: 'ANY_ONE' | 'ALL' };
      assignee: { id: string; name: string };
    }>;
    actions: Array<{
      id: string;
      taskId: string | null;
      action: 'APPROVE' | 'RETURN' | 'REJECT' | 'WITHDRAW' | 'TRANSFER' | 'URGE';
      comment: string | null;
      beforeStatus: string;
      afterStatus: string;
      createdAt: string;
      actor: { id: string; name: string };
      transferSource: { id: string; name: string } | null;
      transferTarget: { id: string; name: string } | null;
    }>;
  };
}

export interface ApprovalDecisionResult {
  id: string;
  status: string;
  version: number;
  currentNodeId: string | null;
  document: { id: string; status: DocumentStatus; version: number };
  tasks: Array<{
    id: string;
    nodeId: string;
    assigneeUserId: string;
    status: ApprovalTaskStatus;
    version: number;
    dueAt: string | null;
  }>;
}

export interface ApprovalFlowSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: string;
  versions: Array<{
    id: string;
    versionNumber: number;
    status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
    effectiveFrom: string;
    effectiveUntil: string | null;
    priority: number;
    revision: number;
    publishedAt: string | null;
  }>;
}

export interface ApprovalFlowVersionDetail {
  id: string;
  flowId: string;
  versionNumber: number;
  status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
  effectiveFrom: string;
  effectiveUntil: string | null;
  priority: number;
  revision: number;
  flow: { id: string; code: string; name: string };
  conditions: Array<{
    id: string;
    organizationId: string;
    includeDescendants: boolean;
    documentType: DocumentType;
    expenseCategoryId: string | null;
    minAmount: string;
    maxAmount: string | null;
    organization: { id: string; code: string; name: string };
  }>;
  nodes: Array<{
    id: string;
    sequence: number;
    name: string;
    nodeType: ApprovalNodeType;
    approvalMode: ApprovalMode;
    candidateRoleCode: RoleCode | null;
    scopeOrganizationId: string | null;
    fixedUserId: string | null;
    expectedDurationHours: number | null;
  }>;
}

export interface ApprovalFlowVersionInput {
  effectiveFrom: string;
  effectiveUntil?: string;
  priority: number;
  conditions: Array<{
    organizationId: string;
    includeDescendants: boolean;
    documentType: DocumentType;
    expenseCategoryId?: string;
    minAmount: string;
    maxAmount?: string;
  }>;
  nodes: Array<{
    sequence: number;
    name: string;
    nodeType: ApprovalNodeType;
    approvalMode: ApprovalMode;
    candidateRoleCode?: RoleCode;
    scopeOrganizationId?: string;
    fixedUserId?: string;
    expectedDurationHours?: number;
  }>;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    if (response.status === 401) throw new Error('登录已失效，请重新登录');
    if (body?.code === 'APPROVAL_409_TASK_CONFLICT')
      throw new Error('该待办已被处理或转交，请刷新后查看最新状态');
    if (body?.code === 'APPROVAL_422_NO_MATCHING_FLOW')
      throw new Error('当前单据没有可用审批流程，请联系集团管理员');
    if (body?.code === 'APPROVAL_409_AMBIGUOUS_RULE')
      throw new Error('当前单据命中多个审批流程，请联系集团管理员处理规则冲突');
    if (body?.code === 'APPROVAL_422_NO_ASSIGNEE')
      throw new Error('审批节点没有有效处理人，请联系集团管理员');
    if (response.status === 409) throw new Error('数据已被其他操作更新，请刷新后重试');
    if (response.status === 429) throw new Error('24 小时内已催办过该任务');
    throw new Error(body?.message ?? `请求失败（${response.status}）`);
  }
  return response.json() as Promise<T>;
}

function mutationHeaders(): Record<string, string> {
  return {
    'X-CSRF-Token': sessionStorage.getItem('hsl-ems-csrf') ?? '',
    'X-Request-Id': `web-${crypto.randomUUID()}`,
    'Idempotency-Key': `web-${crypto.randomUUID()}`,
  };
}

export const p3Api = {
  submit: (type: DocumentType, id: string, version: number) =>
    api<{
      document: { status: 'IN_APPROVAL'; version: number };
      approvalInstance: { id: string; status: 'RUNNING' };
      flow: { id: string; code: string; name: string; versionNumber: number };
      tasks: Array<{
        id: string;
        assigneeUserId: string;
        status: 'PENDING';
        version: number;
        dueAt: string | null;
      }>;
    }>(`/${resourceFor[type]}/${id}/submit`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ version, confirmSnapshot: true }),
    }),
  withdraw: (type: DocumentType, id: string, version: number) =>
    api<{
      document: { id: string; status: 'DRAFT'; version: number };
      approvalInstance: { id: string; status: 'WITHDRAWN' };
    }>(`/${resourceFor[type]}/${id}/withdraw`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ version }),
    }),
  tasks: (view: 'PENDING' | 'DONE', cursor?: string) => {
    const query = new URLSearchParams({ view, limit: '20' });
    if (cursor) query.set('cursor', cursor);
    return api<{
      items: ApprovalTaskSummary[];
      pageInfo: { hasNextPage: boolean; nextCursor: string | null };
    }>(`/approval-tasks?${query}`);
  },
  task: (id: string) => api<ApprovalTaskDetail>(`/approval-tasks/${id}`),
  decide: (
    id: string,
    action: 'approve' | 'return' | 'reject',
    version: number,
    comment?: string,
  ) =>
    api<ApprovalDecisionResult>(`/approval-tasks/${id}/${action}`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ version, ...(comment ? { comment } : {}) }),
    }),
  documentTasks: (documentId: string) =>
    api<{
      documentId: string;
      instance: { id: string; status: string; submissionNumber: number } | null;
      tasks: ApprovalTaskSummary[];
    }>(`/documents/${documentId}/approval-tasks`),
  transferableTasks: () => api<ApprovalTaskSummary[]>('/approval-transfer-tasks'),
  urge: (id: string, version: number) =>
    api<{ task: { id: string; status: string; version: number }; urged: true }>(
      `/approval-tasks/${id}/urge`,
      {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ version }),
      },
    ),
  transfer: (id: string, version: number, targetUserId: string, reason: string) =>
    api<{ task: { id: string; status: 'PENDING'; version: number; assigneeUserId: string } }>(
      `/approval-tasks/${id}/transfer`,
      {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ version, targetUserId, reason }),
      },
    ),
  flows: () => api<ApprovalFlowSummary[]>('/approval-flows'),
  createFlow: (payload: { code: string; name: string; description?: string }) =>
    api<ApprovalFlowSummary>('/approval-flows', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  createFlowVersion: (flowId: string, payload: ApprovalFlowVersionInput) =>
    api<ApprovalFlowVersionDetail>(`/approval-flows/${flowId}/versions`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  flowVersion: (id: string) => api<ApprovalFlowVersionDetail>(`/approval-flow-versions/${id}`),
  validateFlowVersion: (id: string) =>
    api<{ valid: boolean; errors: Array<{ field: string; reason: string; nodeId?: string }> }>(
      `/approval-flow-versions/${id}/validate`,
      { method: 'POST', headers: mutationHeaders(), body: '{}' },
    ),
  publishFlowVersion: (id: string, revision: number) =>
    api<ApprovalFlowVersionDetail>(`/approval-flow-versions/${id}/publish`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ revision }),
    }),
  simulateFlow: (payload: {
    operatingOrgId: string;
    documentType: DocumentType;
    expenseCategoryId?: string;
    amount: string;
    at?: string;
  }) =>
    api<{
      matched: true;
      flowVersionId: string;
      flowCode: string;
      flowName: string;
      versionNumber: number;
      conditionId: string;
      nodes: Array<{
        sequence: number;
        name: string;
        nodeType: ApprovalNodeType;
        approvalMode: ApprovalMode;
      }>;
    }>('/approval-flow-versions/simulate', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
};
